import type { RecurringRevenueReport } from '@vy/types';
import { filterMonthKeysByDateRange } from '../../recurring-revenue-report-page/utils/chart-movements';
import { extractMonthlyColumnKeys } from '../../recurring-revenue-report-page/utils/recurring-revenue-month-keys';

type Row = RecurringRevenueReport[number];

type MonthlyMrrBreakdown = {
  totalMrr: number;
  movement: { type: string; amount: number } | null;
};

const EPSILON = 0.005;

/**
 * Reads the per-month MRR breakdown off a report row. Month keys are of the
 * form `MMM YYYY` (e.g. `Jan 2024`) and can be either a plain number (just
 * total MRR) or an object with `totalMrr` + `movement`.
 */
function getMonthlyBreakdown(row: Row, monthKey: string): MonthlyMrrBreakdown | null {
  const value = (row as Record<string, unknown>)[monthKey];
  if (typeof value === 'number') return { totalMrr: value, movement: null };
  if (typeof value === 'object' && value !== null && 'totalMrr' in value) {
    return value as MonthlyMrrBreakdown;
  }
  return null;
}

function getMonthlyMrr(row: Row, monthKey: string): number {
  return getMonthlyBreakdown(row, monthKey)?.totalMrr ?? 0;
}

type RowProduct = { productId: string; productName?: string };

/**
 * Distinct products that appear in a contract row's monthly MRR breakdowns.
 * Used to build the Product filter options and to match a row against a product
 * selection — the report payload doesn't carry catalogProductIds, so products
 * are read straight off the per-month breakdowns.
 */
export function collectRowProducts(row: Row): RowProduct[] {
  const products = new Map<string, string | undefined>();

  for (const value of Object.values(row as Record<string, unknown>)) {
    if (!value || typeof value !== 'object' || !('products' in value)) continue;
    const breakdownProducts = (value as { products?: RowProduct[] }).products;
    if (!breakdownProducts) continue;
    for (const product of breakdownProducts) {
      if (!products.has(product.productId)) {
        products.set(product.productId, product.productName);
      }
    }
  }

  return Array.from(products, ([productId, productName]) => ({ productId, productName }));
}

/**
 * Rows that churned in at least one month within the date range. Churn is
 * derived from MRR going non-zero → zero between consecutive months, matching
 * `classifyCustomerMovement`'s logic — the backend `movement` field is treated
 * as unreliable everywhere else in the chart pipeline.
 */
export function selectChurnedRowsInRange(
  report: RecurringRevenueReport,
  fromDate: Date | null,
  toDate: Date | null,
): RecurringRevenueReport {
  const allMonthKeys = extractMonthlyColumnKeys(report);
  const monthKeysInRange = filterMonthKeysByDateRange(allMonthKeys, fromDate, toDate);
  if (monthKeysInRange.length === 0) return [];

  const inRange = new Set(monthKeysInRange);

  return report.filter((row) => {
    for (let i = 1; i < allMonthKeys.length; i++) {
      const currKey = allMonthKeys[i];
      if (!inRange.has(currKey)) continue;
      const currMrr = getMonthlyMrr(row, currKey);
      const priorMrr = getMonthlyMrr(row, allMonthKeys[i - 1]);
      if (priorMrr > EPSILON && currMrr <= EPSILON) return true;
    }
    return false;
  });
}

/**
 * Rows that have any MRR activity (totalMrr > 0 or a movement event) in at
 * least one month within the date range. Used by NRR/GRR so the filter
 * options only surface contracts that actually contribute to the metric for
 * the selected period.
 */
export function selectActiveRowsInRange(
  report: RecurringRevenueReport,
  fromDate: Date | null,
  toDate: Date | null,
): RecurringRevenueReport {
  const monthKeysInRange = filterMonthKeysByDateRange(extractMonthlyColumnKeys(report), fromDate, toDate);
  if (monthKeysInRange.length === 0) return [];

  return report.filter((row) =>
    monthKeysInRange.some((monthKey) => {
      const breakdown = getMonthlyBreakdown(row, monthKey);
      if (!breakdown) return false;
      return breakdown.totalMrr > 0 || breakdown.movement !== null;
    }),
  );
}
